import React, { createElement, useEffect, useState } from "react";
import styled from "styled-components";
import {
  ResponsiveScatterPlot,
  ScatterPlotNodeProps,
  ScatterPlotTooltipProps,
} from "@nivo/scatterplot";
import { formatPct, validateLogo } from "../../../utils/formattingUtils";
import { ClusterPoint } from "./util";
import { AvatarProps, AvatarStack } from "../../../components/visuals/Avatar";
import {
  compassLightGreen,
  compassYellow,
  greyText,
  lightGreys,
  lightGreyText,
} from "../../../styles";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { CompassProgressBar } from "../../../components/common/CompassProgressBar";
import { Tag } from "antd";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import clusterIcon from "../../../assets/svg/cluster_icon.svg";
import individualIcon from "../../../assets/svg/reports_list.svg";
import { ElementContainer } from "../../../components/containers/styledComponents";
import { ROUTES } from "../../../routes/routeNames";

const TooltipContainer = styled.div`
  background: white;
  padding: 16px 16px;
  border: 1px solid ${lightGreys.borderLightGrey};
  border-radius: 7px;
  min-width: 200px;
`;

export const ScatterChart = ({
  data,
  onPointClick,
}: ScatterChart): JSX.Element => {
  const [debouncedData, setDebouncedData] = useState(data);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedData(data);
    }, 500);

    return () => clearTimeout(id);
  }, [data]);
  return (
    <div style={{ height: 400, width: "100%" }}>
      <ResponsiveScatterPlot
        data={debouncedData}
        animate
        theme={theme}
        motionConfig="gentle"
        margin={{ top: 40, right: 140, bottom: 40, left: 200 }}
        xScale={{ type: "linear", min: "auto", max: "auto", nice: true }}
        yScale={{ type: "linear", min: "auto", max: "auto", nice: true }}
        axisTop={null}
        axisRight={null}
        nodeComponent={CustomDot}
        useMesh
        tooltip={CustomScatterTooltip}
        onClick={(node): void => {
          if (!node.data.isCluster) {
            const url = `${ROUTES.COMPANY_ENGAGEMENTS}/${node.data.id}`;
            window.open(url, "_blank", "noopener,noreferrer");
          } else {
            onPointClick(node.data.id);
          }
        }}
        axisBottom={{
          legend: "Relative Weight (%)",
          legendOffset: 32,
          legendPosition: "middle",
          tickSize: 6,
          tickPadding: 5,
          tickRotation: 0,
          tickValues: 5,
          format: (v): string => `${formatPct(v)}`,
        }}
        axisLeft={{
          legend: "Material Issues (%)",
          legendOffset: -75,
          legendPosition: "middle",
          tickSize: 6,
          tickPadding: 5,
          tickRotation: 0,
          tickValues: 5,
          format: (v) => `${formatPct(v)}`,
        }}
      />
    </div>
  );
};

enum Cluster {
  Individual = "Individual",
  Cluster = "Cluster",
}

const ICONS_TYPE_MAPPING: Record<Cluster, string> = {
  [Cluster.Cluster]: clusterIcon,
  [Cluster.Individual]: individualIcon,
};

const CustomScatterTooltip = ({
  node,
}: ScatterPlotTooltipProps<ClusterPoint>): JSX.Element => {
  const { data } = node;

  const type = data.isCluster ? Cluster.Cluster : Cluster.Individual;
  const totalTopics = data.total_material_topics;
  const coveredTopics = data.covered_topics;
  const totalWeight = data.items.reduce((sum, item) => sum + item.x, 0);

  return (
    <TooltipContainer>
      <ElementContainer w="100%" ai="center" jc="space-between">
        <Tag
          color={!data.isCluster ? "default" : "geekblue"}
          bordered={false}
          icon={
            <IconContainer p={1} m={1} size={20}>
              <Icon src={ICONS_TYPE_MAPPING[type]} />
            </IconContainer>
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: 14,
          }}
        >
          {type}
        </Tag>
      </ElementContainer>
      <ElementContainer w="100%" ai="center" jc="space-between" mt={6}>
        {data.isCluster ? (
          <>
            <CompassParagraph color={greyText}>Size:</CompassParagraph>
            <Tag
              bordered={false}
              style={{
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {`${data.items.length} Companies`}
            </Tag>
          </>
        ) : (
          <>
            <CompassParagraph color={greyText}>Issues:</CompassParagraph>
            <Tag
              bordered={false}
              style={{
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {`${coveredTopics}/${totalTopics}`}
            </Tag>
          </>
        )}
      </ElementContainer>
      <ElementContainer w="100%" ai="center" jc="space-between" mt={6}>
        <CompassParagraph color={greyText}>{`Weight: `}</CompassParagraph>
        <CompassParagraph>{`${formatPct(totalWeight)}`}</CompassParagraph>
      </ElementContainer>
      <CompassParagraph
        mt={6}
        mb={6}
        color={greyText}
        fontSize={11}
      >{`Coverage: ${formatPct(data.y)}`}</CompassParagraph>
      <CompassProgressBar
        height={8}
        value={data.y * 100}
        color={data.y > 0.5 ? compassLightGreen : compassYellow}
      />
    </TooltipContainer>
  );
};

const CustomDot: React.FC<ScatterPlotNodeProps<ClusterPoint>> = ({ node }) => {
  const BASE_SIZE = 32;
  const cluster = node.data as ClusterPoint;
  const containerWidth = cluster.items.length * BASE_SIZE;

  const [items, setItems] = useState<AvatarProps[]>([]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      const validated = await Promise.all(
        cluster.items.map(async (item, index) => {
          const logoResult =
            index <= 4
              ? await validateLogo(item.image)
              : { isValid: false, url: null };
          return {
            username: item.id,
            image: logoResult.isValid ? logoResult.url : null,
            displayText: null,
            tooltip: true,
            borderColor: lightGreys.borderLightGrey,
          };
        })
      );
      setItems(validated);
    };
    load();
  }, [cluster]);

  return (
    <foreignObject
      x={node.x - BASE_SIZE / 2}
      y={node.y - BASE_SIZE / 2}
      width={containerWidth}
      height={BASE_SIZE}
      style={{ pointerEvents: "none" }}
    >
      {createElement(
        "div",
        { xmlns: "http://www.w3.org/1999/xhtml" },
        <AvatarStack items={items} />
      )}
    </foreignObject>
  );
};

const theme = {
  axis: {
    ticks: {
      text: {
        fill: lightGreyText,
        fontSize: 12,
        fontFamily: "Apercu",
      },
    },
    legend: {
      text: {
        fill: lightGreyText,
        fontSize: 14,
        fontFamily: "Apercu",
        fontWeight: 500,
      },
    },
  },
  grid: {
    line: {
      stroke: "#d3d3d3",
      strokeWidth: 0.5,
      strokeDasharray: "5 6",
    },
  },
};

type ScatterChart = {
  data: { id: string; data: ClusterPoint[] }[];
  isLoading: boolean;
  onPointClick: (node: string) => void;
};
